import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  STORE_NAME_MONITORING_CENTRAL_STATION_DEVICES,
  STORE_NAME_MONITORING_CENTRAL_STATION_FAMILIES,
  STORE_NAME_MONITORING_DISPLAY_DEVICES,
  STORE_NAME_MONITORING_DISPLAY_FAMILIES,
  STORE_NAME_MONITORING_KVM_DEVICES,
  STORE_NAME_MONITORING_KVM_FAMILIES,
  STORE_NAME_MONITORING_PATIENT_MONITOR_DEVICES,
  STORE_NAME_MONITORING_PATIENT_MONITOR_FAMILIES,
  STORE_NAME_MONITORING_TELEMETRY_DEVICES,
  STORE_NAME_MONITORING_TELEMETRY_FAMILIES,
  STORE_NAME_MONITORING_UTILITY_DEVICES,
  STORE_NAME_MONITORING_UTILITY_FAMILIES,
} from "@app/indexed-db-config";
import { Device } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { MonitoringProductCategory } from "@domain/product/monitoring/monitoring-product-category";
import {
  MonitoringProductDataByCategory,
  MonitoringProductMapperService,
} from "@domain/product/monitoring/monitoring-product-mapper.service";
import { environment } from "@environments/environment";
import { OptionDisplay } from "@project/monitoring/option-display.service";
import { ObservableInstanceMapper } from "@utils/observable-instance-mapper";
import { instanceToPlain } from "class-transformer";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { catchError, forkJoin, map, Observable, of, switchMap } from "rxjs";

export enum MonitoringCategory {
  CENTRAL_STATIONS = "CENTRAL_STATIONS",
  DISPLAYS = "DISPLAYS",
  KVMS = "KVMS",
  PATIENT_MONITORS = "PATIENT_MONITORS",
  TELEMETRY = "TELEMETRY",
  UTILITY = "UTILITY",
}

@Injectable({
  providedIn: "root",
})
export class MonitoringProductService {
  private dbService = inject(NgxIndexedDBService);
  private monitoringProductMapper = inject(MonitoringProductMapperService);
  private http = inject(HttpClient);

  public static readonly MONITORING_PRODUCT_DATA_URL = environment.apiUrl + "/monitoring-products";

  private CATEGORY_TO_STORE: Record<string, { families: string; devices: string }> = {
    [MonitoringCategory.PATIENT_MONITORS]: {
      devices: STORE_NAME_MONITORING_PATIENT_MONITOR_DEVICES,
      families: STORE_NAME_MONITORING_PATIENT_MONITOR_FAMILIES,
    },
    [MonitoringCategory.CENTRAL_STATIONS]: {
      devices: STORE_NAME_MONITORING_CENTRAL_STATION_DEVICES,
      families: STORE_NAME_MONITORING_CENTRAL_STATION_FAMILIES,
    },
    [MonitoringCategory.TELEMETRY]: {
      devices: STORE_NAME_MONITORING_TELEMETRY_DEVICES,
      families: STORE_NAME_MONITORING_TELEMETRY_FAMILIES,
    },
    [MonitoringCategory.KVMS]: {
      devices: STORE_NAME_MONITORING_KVM_DEVICES,
      families: STORE_NAME_MONITORING_KVM_FAMILIES,
    },
    [MonitoringCategory.DISPLAYS]: {
      devices: STORE_NAME_MONITORING_DISPLAY_DEVICES,
      families: STORE_NAME_MONITORING_DISPLAY_FAMILIES,
    },
    [MonitoringCategory.UTILITY]: {
      devices: STORE_NAME_MONITORING_UTILITY_DEVICES,
      families: STORE_NAME_MONITORING_UTILITY_FAMILIES,
    },
  };

  init(): Observable<MonitoringProductDataByCategory> {
    const categories = Object.values(MonitoringCategory);

    return this.loadAllCategoriesFromDB().pipe(
      switchMap((productData) => {
        if (this.shouldRefresh(productData, categories, navigator.onLine)) {
          return this.refreshAll();
        }
        return of(productData);
      }),
    );
  }

  getAllProductMatNrs(): Observable<string[]> {
    const deviceObservables: Observable<Device[]>[] = [];
    Object.keys(this.CATEGORY_TO_STORE).forEach((category) => {
      deviceObservables.push(this.loadAllCategoryDevicesFromDB(category));
    });
    return forkJoin(deviceObservables).pipe(
      map((deviceArrays) => deviceArrays.flat()),
      map((devices: Device[]) => devices.map((device) => device.matNr)),
    );
  }

  getDiscontinuedMatNrs(filterCountry: string): Observable<string[]> {
    const deviceObservables: Observable<Device[]>[] = [];
    Object.keys(this.CATEGORY_TO_STORE).forEach((category) => {
      deviceObservables.push(this.loadAllCategoryDevicesFromDB(category));
    });
    return forkJoin(deviceObservables).pipe(
      map((deviceArrays) => deviceArrays.flat()),
      map((devices: Device[]) =>
        devices
          .filter((device) => !device.isAvailable && this.isAvailableInCountry(device, filterCountry))
          .map((device) => device.matNr),
      ),
    );
  }

  loadFamilyFromDB(category: MonitoringCategory, familyNumber: string, filterCountry?: string): Observable<Family | undefined> {
    return ObservableInstanceMapper.valueToInstance(
      this.dbService.getByKey<Family>(this.CATEGORY_TO_STORE[category].families, familyNumber),
      Family,
    ).pipe(
      catchError((err) => {
        console.error(`Failed to get device with number ${familyNumber}`, err);
        return of(undefined);
      }),
      map((family) => {
        if (!family) return undefined;
        if (filterCountry && !this.isAvailableInCountry(family, filterCountry)) return undefined;
        return family;
      }),
    );
  }

  loadCategoryFamiliesFromDB(category: MonitoringCategory, filterCountry?: string): Observable<Family[]> {
    return ObservableInstanceMapper.valuesToInstance(
      this.dbService.getAll<Family>(this.CATEGORY_TO_STORE[category].families),
      Family,
    ).pipe(
      map((families: Family[]) =>
        filterCountry ? families.filter((f) => (f.countries ?? []).includes(filterCountry)) : families,
      ),
      catchError((err) => {
        console.error(`Failed to load families for ${category}`, err);
        return of([]);
      }),
    );
  }

  loadAllCategoryDevicesFromDB(category: string, filterCountry?: string): Observable<Device[]> {
    return ObservableInstanceMapper.valuesToInstance(
      this.dbService.getAll<Device>(this.CATEGORY_TO_STORE[category].devices),
      Device,
    ).pipe(
      map((devices: Device[]) => (filterCountry ? devices.filter((c) => (c.countries ?? []).includes(filterCountry)) : devices)),

      catchError((err) => {
        console.error(`Failed to store devices for ${category}`, err);
        return of([]);
      }),
    );
  }

  loadDeviceFromDB(deviceNumber: string, category: MonitoringCategory, filterCountry?: string): Observable<Device | undefined> {
    return ObservableInstanceMapper.valueToInstance(
      this.dbService.getByKey<Device>(this.CATEGORY_TO_STORE[category].devices, deviceNumber),
      Device,
    ).pipe(
      catchError((err) => {
        console.error(`Failed to get device with number ${deviceNumber}`, err);
        return of(undefined);
      }),
      map((device) => {
        if (!device) return undefined;
        if (filterCountry && !this.isAvailableInCountry(device, filterCountry)) return undefined;
        return device;
      }),
    );
  }

  loadDevicesFromDB(matNr: string[], category: MonitoringCategory, filterCountry: string): Observable<Device[]> {
    if (!matNr.length) return of([]);
    const requests: Observable<Device | undefined>[] = matNr.map((number) =>
      ObservableInstanceMapper.valueToInstance(
        this.dbService.getByKey<Device>(this.CATEGORY_TO_STORE[category].devices, number),
        Device,
      ).pipe(
        catchError((err) => {
          console.error(`Failed to get device with number ${number}`, err);
          return of(undefined);
        }),
        map((device) => {
          if (!device) return undefined;
          if (filterCountry && !this.isAvailableInCountry(device, filterCountry)) return undefined;
          return device;
        }),
      ),
    );

    return forkJoin(requests).pipe(map((devices) => devices.filter((d): d is Device => d !== undefined)));
  }

  searchFilterFamily(option: OptionDisplay, query: string, filterCountry: string): boolean {
    if (!option.family) return false;
    const q = query.toLowerCase();
    return (
      (option.family.familyName.toLowerCase().includes(q) || option.family.familyNumber.toLowerCase().includes(q)) &&
      this.isAvailableInCountry(option.family, filterCountry)
    );
  }

  searchFilterOption(option: OptionDisplay, query: string): boolean {
    const q = query.toLowerCase();

    return (
      option.displayName.toLowerCase().includes(q) || option.option.devices.some((device) => device.toLowerCase().includes(q))
    );
  }

  refreshAll(): Observable<MonitoringProductDataByCategory> {
    return this.loadMonitoringProductDataFromApi().pipe(
      switchMap((products) => this.clearAll().pipe(switchMap(() => this.storeProductsInDB(products)))),
    );
  }

  private shouldRefresh(
    productData: MonitoringProductDataByCategory | null | undefined,
    categories: MonitoringCategory[],
    isOnline: boolean,
  ): boolean {
    if (!productData) return true;

    const isEmpty = categories.every((category) => this.isCategoryEmpty(productData[category]));
    if (isEmpty) return true;

    const hasMissingCategory = categories.some((category) => this.isCategoryMissingOrIncomplete(productData[category]));
    return hasMissingCategory && isOnline;
  }

  private isCategoryEmpty(categoryData?: { families?: unknown[]; devices?: unknown[] }): boolean {
    return (categoryData?.families?.length ?? 0) === 0 && (categoryData?.devices?.length ?? 0) === 0;
  }

  private isCategoryMissingOrIncomplete(categoryData?: { families?: unknown[]; devices?: unknown[] }): boolean {
    return !categoryData || (categoryData.families?.length ?? 0) === 0 || (categoryData.devices?.length ?? 0) === 0;
  }

  private isAvailableInCountry(product: Family | Device, filterCountry: string) {
    if (!filterCountry) return true;
    return (product.countries ?? []).includes(filterCountry);
  }

  private loadAllCategoriesFromDB(): Observable<MonitoringProductDataByCategory> {
    const categoryObservables: Record<MonitoringCategory, Observable<MonitoringProductCategory>> = {} as any;

    Object.values(MonitoringCategory).forEach((category) => {
      categoryObservables[category] = this.loadCategoryFromDB(category);
    });
    return forkJoin(categoryObservables);
  }

  private loadCategoryFromDB(category: MonitoringCategory): Observable<MonitoringProductCategory> {
    return forkJoin({
      families: this.loadCategoryFamiliesFromDB(category),
      devices: this.loadAllCategoryDevicesFromDB(category),
    }).pipe(map(({ families, devices }) => new MonitoringProductCategory(families, devices)));
  }

  private clearAll(): Observable<void[]> {
    return forkJoin([
      this.dbService.clear(STORE_NAME_MONITORING_PATIENT_MONITOR_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_PATIENT_MONITOR_FAMILIES),
      this.dbService.clear(STORE_NAME_MONITORING_CENTRAL_STATION_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_CENTRAL_STATION_FAMILIES),
      this.dbService.clear(STORE_NAME_MONITORING_TELEMETRY_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_TELEMETRY_FAMILIES),
      this.dbService.clear(STORE_NAME_MONITORING_KVM_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_KVM_FAMILIES),
      this.dbService.clear(STORE_NAME_MONITORING_DISPLAY_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_DISPLAY_FAMILIES),
      this.dbService.clear(STORE_NAME_MONITORING_UTILITY_DEVICES),
      this.dbService.clear(STORE_NAME_MONITORING_UTILITY_FAMILIES),
    ]);
  }

  private storeProductsInDB(products: MonitoringProductDataByCategory): Observable<MonitoringProductDataByCategory> {
    if (Object.values(products).some(Boolean)) {
      const insertObservables: Observable<any>[] = [];

      Object.entries(products).forEach(([category, data]) => {
        if (!data || !data.families.length || !data.devices.length) return;
        const stores = this.CATEGORY_TO_STORE[category as MonitoringCategory];

        insertObservables.push(
          this.dbService
            .bulkAdd(
              stores.families,
              data.families.map((family) => this.toFamilyStoreRecord(family)),
            )
            .pipe(
              catchError((err) => {
                console.error(`Failed to store families for ${category}`, err);
                return of(undefined);
              }),
            ),
        );
        insertObservables.push(
          this.dbService
            .bulkAdd(
              stores.devices,
              data.devices.map((device) => instanceToPlain(device)),
            )
            .pipe(
              catchError((err) => {
                console.error(`Failed to store devices for ${category}`, err);
                return of(undefined);
              }),
            ),
        );
      });
      if (insertObservables.length) {
        return forkJoin(insertObservables).pipe(map(() => products));
      }
    }
    return of(products);
  }

  private toFamilyStoreRecord(family: Family) {
    const familyRecord = instanceToPlain(family) as Family;
    const familyNumber = family.familyNumber?.trim();
    const familyName = family.familyName?.trim();

    return {
      ...familyRecord,
      familyNumber: familyNumber || familyName,
    };
  }

  private loadMonitoringProductDataFromApi(): Observable<MonitoringProductDataByCategory> {
    return this.http.get(MonitoringProductService.MONITORING_PRODUCT_DATA_URL).pipe(
      map((result) => {
        return this.monitoringProductMapper.mapProductData(result);
      }),
    );
  }
}
